/**
 * Plot this table (TikZ step 4a; Olivia, design-handoffs/2026-10-07-tikz-figures/
 * README-step4.md): a figure linked to a table. The figure stays an ordinary
 * TikZ figure whose `source` is pgfplots code; what makes it a plot is its
 * `plot` spec, and the editor writes the source again from the table each time
 * the table or the spec changes. Export, the drawing service and Copy LaTeX see
 * plain TikZ, byte for byte; nothing on the server knows about plots.
 *
 * Stored on the figure block (`plot`):
 *   { table: "<table block id>", type: "line" | "scatter" | "bar",
 *     x: { column, header }, series: [{ column, header }, …], legend,
 *     columns: <the table's column count when last read>,
 *     xLabel?, yLabel? (the author's overrides) }
 * Columns are followed by identity, not position (see `resolveColumn`): a moved
 * column is found by its header, a renamed one by its place. Detaching removes
 * the spec; the figure is plain TikZ from then on.
 *
 * The table's numbers are read the way the table reads them (`tableFormat.ts`):
 * a column's unit is stripped, `%`, thousands commas, `$…$` and a bold best are
 * all fine; blank and dash cells (–, —, n/a) are skipped. Series colours are the
 * theme's sequence (`lilia-seq1…8`), so the plot follows the paper.
 */
import { cellText, isSpanned, type Cell } from "@/components/editor/table-grid/tableCells";
import { escapeUnit, headerWithUnit, isBlank, parseNumber, planColumns, stripUnit, type ParsedNumber } from "@/components/editor/table-grid/tableFormat";
import { blockToTableData, type TableBlockContent, type TableData } from "@/components/editor/table-grid/tableModel";

export type PlotType = "line" | "scatter" | "bar";

/** A column as the plot knows it: where it was, and what it was called. */
export interface ColumnRef {
  column: number;
  header: string;
}

export interface PlotSpec {
  /** The table block's id. */
  table: string;
  type: PlotType;
  x: ColumnRef;
  /** The y columns, in legend order. */
  series: ColumnRef[];
  legend: boolean;
  /** The table's column count when the refs were last read (a rename keeps the count). */
  columns: number;
  /** The author's axis labels; absent or null: from the headers. */
  xLabel?: string | null;
  yLabel?: string | null;
}

/** Why a table (or a spec on it) can't be plotted. */
export type PlotProblem =
  | "no-numbers" // no column holds numbers
  | "merged" // the body has merged cells: rows no longer line up with columns
  | "no-series" // nothing left to plot on y
  | "no-points" // the chosen columns share no row with a value in both
  | "x-removed"; // the x column was deleted: the plot stops updating

const PROBLEMS: ReadonlySet<string> = new Set<PlotProblem>(["no-numbers", "merged", "no-series", "no-points", "x-removed"]);

/** A problem rather than a source (both are strings). */
export function isPlotProblem(x: unknown): x is PlotProblem {
  return typeof x === "string" && PROBLEMS.has(x);
}

export interface PlotSeries {
  column: number;
  name: string;
  /** x as written for the source (a number, or a category's position), y as written. */
  points: Array<[string, string]>;
}

export interface PlotData {
  xLabel: string;
  yLabel: string | null;
  /** The x column is text: one category per row, in table order (never sorted). */
  categories: string[] | null;
  series: PlotSeries[];
}

/** Colours repeat after eight series, dashed the second time round and dotted the third. */
const SEQ = 8;
/** Over this many points a series is drawn without markers. */
export const MARKERS_UP_TO = 60;
/** Over this many categories, their labels tilt. */
const TILT_OVER = 8;

// ── Reading the table ───────────────────────────────────────────────────

/** A number as pgfplots reads it, spelled as the table spells it (no float noise). */
function numberText(n: ParsedNumber): string {
  return `${n.sign === "-" ? "-" : ""}${n.int || "0"}${n.frac ? `.${n.frac}` : ""}`;
}

export function columnCount(t: TableData): number {
  return Math.max(t.hasHeader ? t.header.length : 0, ...t.rows.map((r) => r.length), 0);
}

export function headerText(t: TableData, c: number): string {
  return t.hasHeader ? cellText(t.header[c] as Cell | undefined).trim() : "";
}

function hasMergedBody(t: TableData): boolean {
  return t.rows.some((r) => r.some(isSpanned));
}

/** Blank, a dash, or n/a: skipped silently. */
function skipped(text: string): boolean {
  return isBlank(text) || /^n\/?a$/i.test(text.trim());
}

/** The numbers of a column, as read (null for a skipped cell), or null when any cell is text. */
function columnNumbers(t: TableData, c: number, unit: string | null): Array<ParsedNumber | null> | null {
  const out: Array<ParsedNumber | null> = [];
  for (const row of t.rows) {
    const text = stripUnit(cellText(row[c] as Cell | undefined), unit);
    if (skipped(text)) {
      out.push(null);
      continue;
    }
    const n = parseNumber(text);
    if (!n) return null;
    out.push(n);
  }
  return out;
}

/** Columns whose every non-blank cell reads as a number (at least one). */
export function numericColumns(t: TableData): number[] {
  const plan = planColumns(t, columnCount(t));
  return plan.filter((p) => columnNumbers(t, p.index, p.unit)?.some(Boolean)).map((p) => p.index);
}

/** What the dialog says about a column: its values, the cells skipped, its unit. */
export function columnSummary(t: TableData, c: number): { values: number; skipped: number; unit: string | null } {
  const unit = planColumns(t, columnCount(t))[c]?.unit ?? null;
  const nums = columnNumbers(t, c, unit) ?? [];
  const values = nums.filter(Boolean).length;
  return { values, skipped: nums.length - values, unit };
}

/** A column the plot follows: same place and name; else its name elsewhere; else its place, when nothing was added or removed. */
export function resolveColumn(t: TableData, ref: ColumnRef, columnsBefore: number): number | null {
  const n = columnCount(t);
  if (ref.column < n && headerText(t, ref.column) === ref.header) return ref.column;
  const named = Array.from({ length: n }, (_, c) => c).filter((c) => headerText(t, c) === ref.header);
  if (ref.header && named.length === 1) return named[0];
  if (n === columnsBefore && ref.column < n) return ref.column;
  return null;
}

export const columnRef = (t: TableData, column: number): ColumnRef => ({ column, header: headerText(t, column) });

/**
 * What "Plot this table" starts with (README-step4, *What it suggests*): x is the
 * first column whose values only increase, else the first text column, else the
 * first column; every other number column is a series; Line for numeric x, Bar
 * for text; the legend is on with two series or more.
 */
export function defaultSpec(t: TableData, table: string): PlotSpec | PlotProblem {
  if (hasMergedBody(t)) return "merged";
  const numeric = numericColumns(t);
  if (numeric.length === 0) return "no-numbers";
  const n = columnCount(t);
  const plan = planColumns(t, n);
  const increasing = numeric.find((c) => {
    const vals = (columnNumbers(t, c, plan[c]?.unit) ?? []).filter(Boolean).map((v) => v!.value);
    return vals.length >= 2 && vals.every((v, i) => i === 0 || v > vals[i - 1]);
  });
  const firstText = Array.from({ length: n }, (_, c) => c).find((c) => !numeric.includes(c));
  const x = increasing ?? firstText ?? 0;
  const series = numeric.filter((c) => c !== x);
  if (series.length === 0) return "no-series";
  return {
    table,
    type: numeric.includes(x) ? "line" : "bar",
    x: columnRef(t, x),
    series: series.map((c) => columnRef(t, c)),
    legend: series.length > 1,
    columns: n,
  };
}

/** The spec with its columns found again in the table as it is now. */
export interface Resolved {
  spec: PlotSpec;
  /** Series columns no longer in the table, by their last header. */
  dropped: string[];
}

export function resolveSpec(t: TableData, spec: PlotSpec): Resolved | PlotProblem {
  const x = resolveColumn(t, spec.x, spec.columns);
  if (x === null) return "x-removed";
  const dropped: string[] = [];
  const series: ColumnRef[] = [];
  for (const s of spec.series) {
    const c = resolveColumn(t, s, spec.columns);
    if (c === null) dropped.push(s.header || `Column ${s.column + 1}`);
    else series.push(columnRef(t, c));
  }
  return { spec: { ...spec, x: columnRef(t, x), series, columns: columnCount(t) }, dropped };
}

export function plotData(t: TableData, spec: PlotSpec): PlotData | PlotProblem {
  if (hasMergedBody(t)) return "merged";
  const ncol = columnCount(t);
  const plan = planColumns(t, ncol);
  const numeric = new Set(numericColumns(t));
  const x = spec.x.column;
  const series = spec.series.map((s) => s.column).filter((c) => c >= 0 && c < ncol && c !== x && numeric.has(c));
  if (series.length === 0) return "no-series";

  const xNumeric = numeric.has(x);
  const categories: string[] | null = xNumeric ? null : [];
  const xNums = xNumeric ? (columnNumbers(t, x, plan[x]?.unit) ?? []) : [];
  const xs: Array<string | null> = t.rows.map((row, r) => {
    if (xNumeric) return xNums[r] ? numberText(xNums[r]!) : null;
    const label = cellText(row[x]).trim();
    if (skipped(label)) return null;
    categories!.push(label);
    return String(categories!.length);
  });

  const out: PlotSeries[] = series.map((c) => {
    const ys = columnNumbers(t, c, plan[c]?.unit) ?? [];
    return {
      column: c,
      name: headerText(t, c) || `Column ${c + 1}`,
      points: t.rows.flatMap((_, r): Array<[string, string]> => (xs[r] !== null && ys[r] ? [[xs[r]!, numberText(ys[r]!)]] : [])),
    };
  });
  if (out.every((s) => s.points.length === 0)) return "no-points";

  const unitOf = (c: number) => plan[c]?.unit ?? null;
  const xUnit = unitOf(x);
  const xLabel = (spec.xLabel?.trim() && labelLatex(spec.xLabel.trim())) || headerWithUnit(headerText(t, x), xUnit ? escapeUnit(xUnit) : null);
  const yLabelText = (spec.yLabel?.trim() && labelLatex(spec.yLabel.trim())) || yLabel(t, series, unitOf);
  return { xLabel, yLabel: yLabelText, categories, series: out };
}

/**
 * One series: its header with its unit. Several: the header group they all sit
 * under (2g) with their shared unit; with different units, no unit (the legend
 * names the series); otherwise nothing.
 */
function yLabel(t: TableData, series: number[], unitOf: (c: number) => string | null): string | null {
  const units = new Set(series.map(unitOf));
  const shared = units.size === 1 ? [...units][0] : null;
  const unit = shared ? escapeUnit(shared) : null;
  if (series.length === 1) return headerWithUnit(headerText(t, series[0]), unit) || null;
  const group = (t.groups ?? []).find((g) => series.every((c) => c >= g.start && c < g.start + g.span));
  if (group?.label.trim()) return headerWithUnit(group.label.trim(), unit);
  return unit ? `(${unit})` : null;
}

/**
 * An axis label the author typed, as LaTeX: a bare %, & or # would start a
 * comment or break the axis, so they are escaped; maths and commands are kept.
 */
export function labelLatex(text: string): string {
  return text.replace(/(?<!\\)([%&#])/g, "\\$1");
}

/** A generated label as the author reads it: `Top-1 (\%)` shows as `Top-1 (%)`. */
export function labelText(latex: string): string {
  return latex.replace(/\\([%&#_$])/g, "$1");
}

/** Where the y label's text came from, for the dialog ("header", "group header"). */
export function yLabelSource(t: TableData, spec: PlotSpec): "header" | "group header" | null {
  const series = spec.series.map((s) => s.column);
  if (series.length === 1) return headerText(t, series[0]) ? "header" : null;
  return (t.groups ?? []).some((g) => g.label.trim() && series.every((c) => c >= g.start && c < g.start + g.span)) ? "group header" : null;
}

// ── Where the legend goes ───────────────────────────────────────────────

export type LegendCorner = "north east" | "north west" | "south east" | "south west";
const CORNERS: LegendCorner[] = ["north east", "north west", "south east", "south west"];

// The plot prints at \linewidth × 0.6\linewidth (about 345 × 207 pt); the axis box inside it.
const AXIS_W = 290;
const AXIS_H = 145;
const BAR_W = 10;

/** A tick step pgfplots would pick: 1, 2 or 5 times a power of ten, about five ticks. */
export function tickStep(range: number): number {
  if (!(range > 0)) return 1;
  const raw = range / 5;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const n = raw / mag;
  return (n <= 1 ? 1 : n <= 2 ? 2 : n <= 5 ? 5 : 10) * mag;
}

/**
 * The corner the data crosses least (README-step4, *Legend placement*): points,
 * line segments and whole bar bodies count, at the printed proportions. When
 * every corner is crossed, the y axis gains headroom one tick at a time (up to
 * three) until one is clear; that becomes `ymax`. The legend never covers data.
 */
export function legendPlacement(d: PlotData, type: PlotType): { corner: LegendCorner; ymax: number | null } {
  const pts = d.series.map((s) => s.points.map(([x, y]) => [Number(x), Number(y)] as const));
  const all = pts.flat();
  const xsAll = all.map((p) => p[0]);
  const ysAll = all.map((p) => p[1]);
  let xlo = Math.min(...xsAll);
  let xhi = Math.max(...xsAll);
  if (d.categories) {
    const pad = Math.max(0.15 * (d.categories.length - 1), 0.5);
    xlo = 1 - pad;
    xhi = d.categories.length + pad;
  } else if (xhi === xlo) {
    xlo -= 1;
    xhi += 1;
  }
  const ylo = Math.min(...ysAll, ...(type === "bar" ? [0] : []));
  const top = Math.max(...ysAll);

  const nameW = Math.max(...d.series.map((s) => s.name.length)) * 4.6 + 26;
  const lw = Math.min(0.9, nameW / AXIS_W) + 0.03;
  const lh = Math.min(0.9, (d.series.length * 13 + 8) / AXIS_H) + 0.03;

  const crossings = (yhi: number) => {
    const nx = (x: number) => (x - xlo) / (xhi - xlo || 1);
    const ny = (y: number) => (y - ylo) / (yhi - ylo || 1);
    return CORNERS.map((corner) => {
      const [x0, x1] = corner.endsWith("east") ? [1 - lw, 1] : [0, lw];
      const [y0, y1] = corner.startsWith("north") ? [1 - lh, 1] : [0, lh];
      const inside = (x: number, y: number) => x >= x0 && x <= x1 && y >= y0 && y <= y1;
      let n = 0;
      pts.forEach((series, si) => {
        if (type === "bar") {
          const offset = ((si - (pts.length - 1) / 2) * BAR_W) / AXIS_W;
          const half = BAR_W / 2 / AXIS_W;
          for (const [x, y] of series) {
            const bx0 = nx(x) + offset - half;
            const bx1 = nx(x) + offset + half;
            const by0 = Math.min(ny(0), ny(y));
            const by1 = Math.max(ny(0), ny(y));
            if (bx1 >= x0 && bx0 <= x1 && by1 >= y0 && by0 <= y1) n++;
          }
          return;
        }
        for (const [x, y] of series) if (inside(nx(x), ny(y))) n++;
        if (type === "line") {
          for (let i = 1; i < series.length; i++) {
            const [ax, ay] = series[i - 1];
            const [bx, by] = series[i];
            for (let k = 1; k < 24; k++) {
              const f = k / 24;
              if (inside(nx(ax + (bx - ax) * f), ny(ay + (by - ay) * f))) {
                n++;
                break;
              }
            }
          }
        }
      });
      return n;
    });
  };

  const step = tickStep(top - ylo);
  let best = { corner: CORNERS[0], ymax: null as number | null, count: Infinity };
  for (let k = 0; k <= 3; k++) {
    const yhi = k === 0 ? top : (Math.floor(top / step + 1e-9) + k) * step;
    const counts = crossings(yhi);
    const min = Math.min(...counts);
    if (min < best.count) best = { corner: CORNERS[counts.indexOf(min)], ymax: k === 0 ? null : yhi, count: min };
    if (min === 0) break;
  }
  return { corner: best.corner, ymax: best.ymax };
}

function numberOut(v: number): string {
  return String(Math.round(v * 1e6) / 1e6);
}

// ── The source ──────────────────────────────────────────────────────────

/** The first line of a plot's source: says where it comes from. */
export function plotComment(tableName: string): string {
  return `% Plotted from ${tableName} by Lilia. Editing here stops it following the table.`;
}

/** The pgfplots source for a spec on a table, or why there is none. */
export function plotSource(t: TableData, spec: PlotSpec, tableName = "a table"): string | PlotProblem {
  const d = plotData(t, spec);
  if (isPlotProblem(d)) return d;

  const opts: string[] = [];
  if (spec.type === "bar") opts.push("ybar");
  if (d.xLabel) opts.push(`xlabel={${d.xLabel}}`);
  if (d.yLabel) opts.push(`ylabel={${d.yLabel}}`);
  if (d.categories) {
    opts.push(`xtick={${d.categories.map((_, i) => i + 1).join(",")}}`);
    opts.push(`xticklabels={${d.categories.map((c) => `{${c}}`).join(",")}}`);
    if (d.categories.length > TILT_OVER) opts.push("x tick label style={rotate=40, anchor=east}");
    opts.push("enlarge x limits=0.15");
  } else {
    // Whole-number x (epochs, years, batch sizes), not many of them: a tick on each, not at 1.5.
    const xs = new Set(d.series.flatMap((s) => s.points.map(([x]) => x)));
    if (xs.size <= 12 && [...xs].every((x) => /^-?\d+$/.test(x))) opts.push("xtick=data");
  }
  if (spec.type === "bar") opts.push("ymin=0");
  if (spec.legend) {
    const { corner, ymax } = legendPlacement(d, spec.type);
    if (ymax !== null) opts.push(`ymax=${numberOut(ymax)}`);
    opts.push(`legend pos=${corner}`);
  }

  const lines = [plotComment(tableName), "\\begin{tikzpicture}"];
  const head = "  \\begin{axis}[width=\\linewidth, height=0.6\\linewidth";
  if (!opts.length) lines.push(`${head}]`);
  else {
    lines.push(`${head},`);
    for (let i = 0; i < opts.length; i += 3) {
      const last = i + 3 >= opts.length;
      lines.push(`      ${opts.slice(i, i + 3).join(", ")}${last ? "]" : ","}`);
    }
  }
  d.series.forEach((s, i) => {
    const colour = `lilia-seq${(i % SEQ) + 1}`;
    const round = Math.floor(i / SEQ);
    const dash = round === 1 ? ", dashed" : round >= 2 ? ", dotted" : "";
    let style: string;
    if (spec.type === "bar") style = `fill=${colour}, draw=${colour}`;
    else if (spec.type === "scatter" || s.points.length === 1) style = `color=${colour}, only marks, mark=*`;
    else style = `color=${colour}, ${s.points.length > MARKERS_UP_TO ? "no markers" : "mark=*"}`;
    lines.push(`    \\addplot[${style}${spec.type === "bar" ? "" : dash}] coordinates {${s.points.map(([x, y]) => `(${x},${y})`).join(" ")}};`);
    if (spec.legend) lines.push(`    \\addlegendentry{${s.name}}`);
  });
  lines.push("  \\end{axis}", "\\end{tikzpicture}");
  return lines.join("\n");
}

/** The words for a problem, for the dialog and the figure. */
export function problemText(p: PlotProblem, tableName = "This table"): string {
  switch (p) {
    case "no-numbers":
      return `${tableName} has no number columns.`;
    case "merged":
      return `${tableName} has merged cells, so its rows don't line up with its columns. Unmerge them to plot it.`;
    case "no-series":
      return "Choose at least one column of numbers to plot.";
    case "no-points":
      return "The chosen columns share no row with a value in each.";
    case "x-removed":
      return `The x column was removed from ${tableName}, so the plot no longer updates.`;
  }
}

function readRef(raw: unknown): ColumnRef | null {
  if (typeof raw === "number" && Number.isInteger(raw) && raw >= 0) return { column: raw, header: "" };
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.column !== "number" || !Number.isInteger(r.column) || r.column < 0) return null;
  return { column: r.column, header: typeof r.header === "string" ? r.header : "" };
}

/** A stored spec, read defensively (it comes from the block's JSON). */
export function readPlotSpec(raw: unknown): PlotSpec | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  if (typeof r.table !== "string" || !r.table) return null;
  const type: PlotType = r.type === "scatter" || r.type === "bar" ? r.type : "line";
  const x = readRef(r.x) ?? { column: 0, header: "" };
  const series = Array.isArray(r.series) ? r.series.map(readRef).filter((s): s is ColumnRef => s !== null) : [];
  const columns = typeof r.columns === "number" && r.columns > 0 ? r.columns : 0;
  return {
    table: r.table,
    type,
    x,
    series,
    legend: r.legend !== false,
    columns,
    ...(typeof r.xLabel === "string" && r.xLabel.trim() ? { xLabel: r.xLabel } : {}),
    ...(typeof r.yLabel === "string" && r.yLabel.trim() ? { yLabel: r.yLabel } : {}),
  };
}

// ── The linked table, in the editor's document ──────────────────────────

/** A table node as the editor holds it: its attrs are the block's content. */
export interface DocTable {
  blockId: string;
  attrs: Record<string, unknown>;
}

/** The document's tables in order. */
export function docTables(doc: { forEach?: (f: (node: { type: { name: string }; attrs: Record<string, unknown> }) => void) => void } | null | undefined): DocTable[] {
  const out: DocTable[] = [];
  doc?.forEach?.((node) => {
    if (node.type.name === "tableBlock" && typeof node.attrs.blockId === "string") out.push({ blockId: node.attrs.blockId, attrs: node.attrs });
  });
  return out;
}

export function tableData(t: DocTable): TableData {
  return blockToTableData(t.attrs as TableBlockContent);
}

/** "Table 2" (tables with a caption are numbered, as in the PDF), or "a table". */
export function tableName(tables: readonly DocTable[], blockId: string): string {
  let n = 0;
  for (const t of tables) {
    const numbered = typeof t.attrs.caption === "string" && !!t.attrs.caption.trim();
    if (numbered) n++;
    if (t.blockId === blockId) return numbered ? `Table ${n}` : "a table";
  }
  return "a table";
}

export type LinkedPlot =
  /** `spec` differs from the stored one when a column moved or was renamed or removed. */
  | { state: "linked"; table: string; source: string; spec: PlotSpec; dropped: string[] }
  | { state: "problem"; table: string; problem: PlotProblem }
  /** The table was deleted: the figure keeps its last source and stops updating. */
  | { state: "missing" };

/** What a linked figure should show now. */
export function resolveLinkedPlot(tables: readonly DocTable[], spec: PlotSpec): LinkedPlot {
  const t = tables.find((x) => x.blockId === spec.table);
  if (!t) return { state: "missing" };
  const name = tableName(tables, spec.table);
  const data = tableData(t);
  const resolved = resolveSpec(data, spec);
  if (isPlotProblem(resolved)) return { state: "problem", table: name, problem: resolved };
  const src = plotSource(data, resolved.spec, name);
  return isPlotProblem(src)
    ? { state: "problem", table: name, problem: src }
    : { state: "linked", table: name, source: src, spec: resolved.spec, dropped: resolved.dropped };
}

/** The legend corner a written source chose (`legend pos=north west`), for the settings' note. */
export function legendCornerOf(source: string): LegendCorner | null {
  const m = /legend pos=(north east|north west|south east|south west)/.exec(source);
  return m ? (m[1] as LegendCorner) : null;
}
